<template>
  <main class="wallet-nft-send-page">
    <AppNavBar :title="t('wallet.nftSendTitle')" />

    <section class="wallet-card">
      <h2>{{ t('wallet.receiveAddress') }}</h2>
      <van-field
        v-model="addressTo"
        clearable
        :placeholder="t('wallet.enterOrPasteAddress')"
      >
        <template #button>
          <van-icon name="scan" @click="openScanner" />
        </template>
      </van-field>
    </section>

    <section class="wallet-card">
      <h2>{{ t('wallet.nftSendAsset') }}</h2>

      <div class="asset-row">
        <div class="asset-row__cover">
          <app-image v-if="assetInfo.img" :src="assetInfo.img" alt="" />
          <div v-else class="asset-row__cover-fallback">NFT</div>
        </div>

        <div class="asset-row__meta">
          <div data-strong>{{ nftName }}</div>
          <span>#{{ tokenIdText }}</span>
        </div>
      </div>
    </section>

    <van-button block round type="primary" class="submit-btn" @click="submitTransfer">
      {{ t('wallet.sendNft') }}
    </van-button>

    <PayPasswordPopup
      v-model:show="showPayPassword"
      v-model:password="payPassword"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      :show-forgot="true"
      :confirm-disabled="!payPassword.trim()"
      @confirm="confirmPassword"
      @cancel="resetPasswordPopup"
      @closed="resetPasswordPopup"
      @forgot="goForgotPassword"
    />
  </main>
</template>

<script setup>
import { computed, watch, ref } from 'vue';
import { closeToast, showFailToast, showLoadingToast, showSuccessToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Contract, JsonRpcProvider, Wallet, isAddress } from 'ethers';
import PayPasswordPopup from '@/components/PayPasswordPopup.vue';
import { payPwdCheck } from '@/api/auth';
import { useProfileStore } from '@/stores/profile';
import { useWalletNftStore } from '@/stores/wallet-nft';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import { getWalletSignerSecret } from '@/utils/walletSession';
import { derivePrivateKeyByNetwork, resolveNetworkType } from '@/utils/walletAddress';
import { useWalletScanner } from '@/composables/useWalletScanner';

const NFT_CONTRACT_ABI = [
  'function transferFrom(address from, address to, uint256 tokenId)',
  'function ownerOf(uint256 tokenId) view returns (address owner)'
];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const walletNftStore = useWalletNftStore();
const { openWalletScanner } = useWalletScanner();
const pageState = computed(() => getRouteState(route, 'wallet-nft-send', {}));

const addressTo = ref('');
const showPayPassword = ref(false);
const payPassword = ref('');

const assetInfo = computed(() => walletNftStore.currentNftAsset || {});
const nftInfo = computed(() => walletNftStore.currentNft || {});
const nftName = computed(() => String(assetInfo.value?.name || nftInfo.value?.name || nftInfo.value?.symbol || 'NFT'));
const tokenIdText = computed(() => String(assetInfo.value?.tokenId || ''));
const contractAddress = computed(() => String(assetInfo.value?.contractAddress || nftInfo.value?.contractAddress || '').trim());
const networkType = computed(() => resolveNetworkType(profileStore.currentNetwork || {}));

watch(
  () => pageState.value.scanResult,
  (value) => {
    if (!value) {
      return;
    }
    addressTo.value = String(value).split('&')[0];
    navigateWithState(router, route.path, {
      namespace: 'wallet-nft-send',
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: ''
      }
    });
  },
  { immediate: true }
);

function resetPasswordPopup() {
  payPassword.value = '';
  showPayPassword.value = false;
}

function openScanner() {
  openWalletScanner({
    back: '/wallet/nft-send',
    stateNamespace: 'wallet-nft-send',
    statePayload: {
      ...pageState.value
    }
  });
}

function goForgotPassword() {
  resetPasswordPopup();
  router.push('/auth/forgot-pay-password');
}

function ensureReady() {
  if (!tokenIdText.value || !contractAddress.value) {
    router.replace('/wallet/nft-detail');
    return false;
  }

  if (String(profileStore.currentAccount?.type || '') === '1') {
    showFailToast(t('article.observerWalletWarn'));
    return false;
  }

  if (networkType.value !== 'evm') {
    showFailToast(t('wallet.nftTransferUnsupported'));
    return false;
  }

  return true;
}

function submitTransfer() {
  if (!ensureReady()) {
    return;
  }

  if (!addressTo.value.trim()) {
    showFailToast(t('wallet.enterOrPasteAddress'));
    return;
  }

  if (!isAddress(addressTo.value.trim())) {
    showFailToast(t('wallet.invalidAddress'));
    return;
  }

  showPayPassword.value = true;
}

async function sendNftTransaction() {
  const signerPayload = getWalletSignerSecret(profileStore.accountStorageAddress);
  if (!signerPayload?.secret) {
    throw new Error(t('wallet.privateKeyUnavailable'));
  }

  const rpcUrl = String(
    profileStore.currentNetwork?.userRpcUrl
      || profileStore.currentNetwork?.rpcUrl
      || ''
  ).trim();

  if (!rpcUrl) {
    throw new Error(t('common.notAvailableYet'));
  }

  const privateKey = await derivePrivateKeyByNetwork(
    signerPayload.secret,
    signerPayload.type,
    profileStore.currentNetwork
  );

  const provider = new JsonRpcProvider(rpcUrl);
  const signer = new Wallet(privateKey, provider);
  const contract = new Contract(contractAddress.value, NFT_CONTRACT_ABI, signer);
  const tx = await contract.transferFrom(
    signer.address,
    addressTo.value.trim(),
    BigInt(tokenIdText.value)
  );
  await tx.wait();
  return tx.hash;
}

async function confirmPassword() {
  const payPwd = String(payPassword.value || '').trim();
  if (!payPwd) {
    showFailToast(t('auth.enterPassword'));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t('auth.passwordError'));
    return;
  }

  resetPasswordPopup();
  showLoadingToast({
    message: t('common.loading'),
    forbidClick: true,
    duration: 0
  });

  try {
    await sendNftTransaction();
    await walletNftStore.fetchNftList(profileStore.platformAddress || '', { meta: { showLoading: false } });
    closeToast();
    showSuccessToast(t('wallet.nftSendSuccess'));
    router.go(-2);
  } catch (error) {
    closeToast();
    showFailToast(error?.reason || error?.shortMessage || error?.message || t('common.retryLater'));
  }
}

if (!assetInfo.value?.tokenId && !assetInfo.value?.img) {
  router.replace('/wallet/nft-detail');
}
</script>

<style scoped lang="scss">
.wallet-nft-send-page {
  min-height: var(--app-viewport-height);
  padding: 0 15px 34px;
  background: #f7f7f7;
}

.wallet-card {
  margin-top: 10px;
  padding: 13px 16px;
  border-radius: 12px;
  background: #ffffff;
}

.wallet-card h2 {
  margin: 0 0 14px;
  color: #111111;
  font-size: 16px;
  font-weight: 500;
}

.asset-row {
  display: flex;
  align-items: center;
}

.asset-row__cover {
  width: 70px;
  height: 70px;
  margin-right: 10px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;

  .app-image,
  .asset-row__cover-fallback {
    width: 100%;
    height: 100%;
  }

  .app-image {
    object-fit: cover;
  }
}

.asset-row__cover-fallback {
  display: grid;
  place-items: center;
  color: #999;
  font-size: 16px;
  font-weight: 700;
}

.asset-row__meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.asset-row__meta [data-strong] {
  color: #111111;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.asset-row__meta span {
  color: #999999;
  font-size: 14px;
  line-height: 20px;
}

.submit-btn {
  margin-top: 20px;
}
</style>
